import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatRoundFixed } from '@benzinga/utils/numberUtils';
import { formatLarge } from '@benzinga/utils/numberUtils';
import type { Earnings } from '@benzinga/calendar-manager';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { EarningsSummary } from '../../../utils/quote/summaries';
import { formatTickerExchange } from '../../../../utils/exchange';
import { formatDate, isPastDate } from './utils';
import FAQs from '../../../components/FAQs';

interface EarningsFAQProps {
  earningsSummary: EarningsSummary | null;
  quote: DelayedQuote | null;
}

export const EarningsFAQ: React.FC<EarningsFAQProps> = ({ earningsSummary, quote }) => {
  const { t, i18n } = useTranslation('quote');
  const language = i18n.language;
  const futureEarning = earningsSummary?.earnings?.find(earning => !isPastDate(DateTime.fromISO(earning.date))) ?? null;
  const earningsSummaryLast = earningsSummary?.last ?? null;

  const FAQsDataSet = [
    {
      answer: getEarningInfo(earningsSummary?.earnings ?? [], quote, futureEarning, t, language),
      question: t('Quote.FAQ.Questions.when-is-reporting-earnings', {
        exchange: formatTickerExchange(quote?.bzExchange, quote?.otcTier),
        quoteName: quote?.name,
        symbol: quote?.symbol,
      }),
    },
    {
      answer: getLatestEarningEPSInfo(earningsSummaryLast, quote, futureEarning, t, language),
      question: t('Quote.FAQ.Questions.what-were-the-latest-earnings', {
        exchange: formatTickerExchange(quote?.bzExchange, quote?.otcTier),
        quoteName: quote?.name,
        symbol: quote?.symbol,
      }),
    },
    {
      answer: getRevenuesInfo(earningsSummaryLast, quote, futureEarning, t, language),
      question: t('Quote.FAQ.Questions.what-were-revenues', {
        exchange: formatTickerExchange(quote?.bzExchange, quote?.otcTier),
        quoteName: quote?.name,
        symbol: quote?.symbol,
      }),
    },
  ];

  const title = `${quote?.name}`;

  return <FAQs FAQsDataSet={FAQsDataSet} title={title} />;
};

export default EarningsFAQ;

const getEarningInfo = (
  earnings: Earnings[],
  quote: DelayedQuote | null,
  futureEarning: Earnings | null | undefined,
  t: TFunction,
  language: string,
): React.ReactElement => {
  if (!earnings || earnings.length === 0) {
    return <p>{t('Quote.FAQ.Answers.there-are-no-earnings-for', { quoteName: quote?.name })}</p>;
  }

  const pastEarnings = earnings.filter((e: Earnings) => isPastDate(DateTime.fromISO(e.date)));
  const pastEarning = pastEarnings[0];
  if (pastEarning) {
    const pastDate = DateTime.fromISO(pastEarning.date).setLocale(language);

    if (futureEarning) {
      const futureDate = DateTime.fromISO(futureEarning.date).setLocale(language);
      return (
        <p>
          {t('Quote.FAQ.Answers.future-earning', {
            futureDate: formatDate(futureDate),
            pastDate: formatDate(pastDate),
            period: pastEarning.period,
            quoteName: quote?.name,
            quoteSymbol: quote?.symbol,
          })}
        </p>
      );
    }
    return (
      <p>
        {t('Quote.FAQ.Answers.earnings-not-scheduled', {
          pastDate: formatDate(pastDate),
          period: pastEarning.period,
          quoteName: quote?.name,
          quoteSymbol: quote?.symbol,
        })}
      </p>
    );
  }
  return <p>{t('Quote.FAQ.Answers.there-are-no-earnings-for', { quoteName: quote?.name })}</p>;
};

const getLatestEarningEPSInfo = (
  lastEarning: Earnings | null,
  quote: DelayedQuote | null,
  futureEarning: Earnings | null,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = (
    <p>{t('Quote.FAQ.Answers.there-are-no-earnings-for', { quoteName: quote?.name })}</p>
  );
  if (!lastEarning) {
    return fallbackValue;
  }

  try {
    const date = DateTime.fromISO(lastEarning.date).setLocale(language);
    const eps = Number(lastEarning.eps);
    const epsEst = Number(lastEarning.eps_est);
    const actualEPS = `$${formatRoundFixed(eps, 2)}`;
    const estEPS = `$${formatRoundFixed(epsEst, 2)}`;
    let type = '';
    const nextDate = futureEarning ? DateTime.fromISO(futureEarning.date) : null;

    if (eps > epsEst) {
      type = t('Quote.EPS.Type.beat');
    } else if (eps < epsEst) {
      type = t('Quote.EPS.Type.missed');
    } else {
      type = t('Quote.EPS.Type.hit');
    }

    if (nextDate) {
      return <p>{t('Quote.FAQ.Answers.the-actual-eps-was', { actualEPS, estEPS, type })}</p>;
    }
    if (isPastDate(date)) {
      return (
        <p>
          {t('Quote.FAQ.Answers.does-not-have-any-upcoming-earnings', {
            actualEPS,
            date: formatDate(date),
            estEPS,
            period: lastEarning.period,
            quoteName: quote?.name,
            quoteSymbol: quote?.symbol,
            type,
          })}
        </p>
      );
    }
    return (
      <p>
        {t('Quote.FAQ.Answers.will-be-reporting-earnings-on', {
          date: formatDate(date),
          quoteName: quote?.name,
          quoteSymbol: quote?.symbol,
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};

const getRevenuesInfo = (
  lastEarning: Earnings | null,
  quote: DelayedQuote | null,
  futureEarning: Earnings | null,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const fallbackValue = (
    <p>{t('Quote.FAQ.Answers.there-are-no-earnings-for', { quoteName: quote?.name })}</p>
  );
  if (!lastEarning) {
    return fallbackValue;
  }

  try {
    const date = DateTime.fromISO(lastEarning.date).setLocale(language);
    const rev = Number(lastEarning.revenue);
    const revEst = Number(lastEarning.revenue_est);
    const actualREV = `$${formatLarge(rev)}`;
    const estREV = `$${formatLarge(revEst)}`;
    const nextDate = futureEarning ? DateTime.fromISO(futureEarning.date) : null;
    let type = '';

    if (rev > revEst) {
      type = t('Quote.EPS.Type.beat');
    } else if (rev < revEst) {
      type = t('Quote.EPS.Type.missed');
    } else {
      type = t('Quote.EPS.Type.hit');
    }

    if (nextDate) {
      return <p>{t('Quote.FAQ.Answers.the-actual-revenue-was', { actualREV, estREV, type })}</p>;
    }
    if (isPastDate(date)) {
      return (
        <p>
          {t('Quote.FAQ.Answers.does-not-have-any-upcoming-earnings-revenue', {
            actualREV,
            date: formatDate(date),
            estREV,
            period: lastEarning.period,
            quoteName: quote?.name,
            quoteSymbol: quote?.symbol,
            type,
          })}
        </p>
      );
    }
    return (
      <p>
        {t('Quote.FAQ.Answers.will-be-reporting-earnings-on', {
          date: formatDate(date),
          quoteName: quote?.name,
          quoteSymbol: quote?.symbol,
        })}{' '}
        {t('Quote.FAQ.Answers.the-last-reported-revenue', {
          actualREV,
          period: lastEarning.period,
        })}
      </p>
    );
  } catch (error) {
    return fallbackValue;
  }
};
